import React, { useCallback, useMemo } from "react"
import {
  Avatar,
  Box,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material"

import { Head } from "components/Head"
import { useTranslation } from "providers/i18n"
import { ImageCard } from "../../components/ImageCard"
import { useVenue } from "../../providers/venue"
import { useDining } from "hooks/dining/useDining"
import { HorizontalScrollPane } from "components/HorizontalScrollPane"
import _ from "lodash"
import { CustomIcon, DiningIcon } from "../../components/icons"
import { getFeatureDetail } from "utils/feature"
import { Link } from "react-router-dom"
import { EmptyData } from "components/EmptyData"
import { getIcon } from "utils/icon"

const DEFAULT_FILTER_ICON = <DiningIcon />

export const DiningPage = () => {
  const { occupants, isMultiOrdinalVenue } = useVenue()
  const { restaurants, currentCategory, categories, onChangeCategory } =
    useDining(occupants)
  const { t } = useTranslation({ defaultValue: null })

  const filterItems = useMemo(
    () => [
      { value: "ALL", label: t("All"), icon: DEFAULT_FILTER_ICON },
      ...categories.map((category) => ({
        value: category.id,
        label: category.properties.name,
        icon: category.properties?.icon_path ? (
          <CustomIcon path={category.properties?.icon_path} />
        ) : (
          DEFAULT_FILTER_ICON
        ),
      })),
    ],
    [categories, t]
  )

  const handleChangeCategory = useCallback(
    (e, id) => {
      if (id === null) return
      onChangeCategory(id)
    },
    [onChangeCategory]
  )

  const theme = useTheme()
  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.dining) || <DiningIcon />,
    [theme]
  )
  const showFilterBar = filterItems.length > 1
  const { styleOverrides = {} } = theme.components?.[`AppDining`] || {}
  const {
    filterSection,
    filterContainer,
    diningImageCard,
    filterButton,
    diningAvatar,
    diningAvatarFallBack,
    emptyDataContainer,
  } = styleOverrides

  return (
    <>
      <Head title={t("Dining")} />
      <Box>
        {showFilterBar && (
          <Box sx={filterSection}>
            <HorizontalScrollPane sx={filterContainer}>
              <ToggleButtonGroup
                value={currentCategory}
                onChange={handleChangeCategory}
                exclusive
              >
                {filterItems.map((item) => (
                  <ToggleButton
                    value={item.value}
                    key={item.value}
                    sx={filterButton}
                  >
                    {item.icon}
                    <Typography>{t(item.label)}</Typography>
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </HorizontalScrollPane>
          </Box>
        )}
        {restaurants.length > 0 ? (
          <Grid container spacing={1}>
            {restaurants.map((restaurant) => {
              const { group, level, logo, featuredImage, name, isFeatured } =
                getFeatureDetail(restaurant) || {}
              const levelText = isMultiOrdinalVenue
                ? t("Level Text", { level: t(level) })
                : ""
              const groupText = t(group)
              const fallBackIcon = _.first(
                restaurant?.properties?.local_parent_categories
              )?.properties?.icon_path
              return (
                <Grid item xs={6} md={4} lg={3} key={restaurant?.id}>
                  <ImageCard
                    sx={diningImageCard}
                    component={Link}
                    to={`/maps/place/${restaurant?.id}`}
                    title={t(name)}
                    subheader={_.compact([levelText, groupText]).join(" ")}
                    image={featuredImage}
                    avatar={
                      <Avatar src={logo} sx={diningAvatar}>
                        <CustomIcon
                          path={fallBackIcon}
                          sx={diningAvatarFallBack}
                        />
                      </Avatar>
                    }
                    tag={isFeatured ? t("Highlight") : null}
                  />
                </Grid>
              )
            })}
          </Grid>
        ) : (
          <Box sx={emptyDataContainer}>
            <EmptyData
              icon={emptyIcon}
              primary={t("Coming soon")}
              secondary={t("Stay tuned for the update")}
            />
          </Box>
        )}
      </Box>
    </>
  )
}
